import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile, NotificationPreferences } from "@/types/profile";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "@/hooks/use-toast";

export function useProfileData() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: profile,
    isLoading,
    error
  } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('No user ID');

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      // Convert the notification_preferences from Json to NotificationPreferences
      const notificationPrefs = data.notification_preferences as Record<string, boolean> || {};

      return {
        ...data,
        notification_preferences: {
          email: notificationPrefs.email ?? true,
          push: notificationPrefs.push ?? true,
          is_admin: notificationPrefs.is_admin ?? false
        } as NotificationPreferences
      } as Profile;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user?.id) throw new Error('No user ID');

      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          notification_preferences: updates.notification_preferences 
            ? JSON.parse(JSON.stringify(updates.notification_preferences))
            : undefined
        })
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast({
        title: "Profil oppdatert",
        description: "Dine endringer har blitt lagret",
      });
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      toast({
        variant: "destructive",
        title: "Feil ved oppdatering",
        description: "Kunne ikke oppdatere profilen. Prøv igjen senere.",
      });
    },
  });

  return {
    profile,
    isLoading,
    error,
    updateProfile: updateProfileMutation.mutateAsync,
    isUpdating: updateProfileMutation.isPending
  };
}